/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { EventCallbackDisposer } from 'src/core/types/eventBusEvent';
import { HandlerGroupFactoryDeps } from 'src/core/types/handler';
import { getEventBus } from 'src/core/utils/eventBus';
import { ToastStore } from 'src/luis/core/stores/ToastStore';

export type ToastHandlerGroup = ReturnType<typeof createToastHandlerGroup>;

const disposers: EventCallbackDisposer[] = [];

/**
 * Creates a toast handler to manage toasts CRUD
 * operations. Creation is done through the event bus,
 * since handlers should not be able to call the dispatcher.
 *
 * @param dependencies The handler group dependencies.
 */
export const createToastHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const eventBus = getEventBus();
    const { rootStore } = dependencies;

    disposers.forEach(disposer => disposer());

    /**
     * Registers a new callback on the `addToast` event to
     * create a new toast store and save in the application
     * state when the event is eventually triggered.
     */
    disposers.push(
        eventBus.on('addToast', args => {
            const notificationStore = new ToastStore(args.id, args);
            rootStore.toastStoreHouse.add(notificationStore);
        })
    );

    /**
     * Registers a new callback on the `updateToast` event to
     * update an existing toast store with the given data and
     * replace it in the application state when the event is
     * eventually triggered.
     */
    disposers.push(
        eventBus.on('updateToast', args => {
            const { id, toast } = args;
            const currentToastStore = rootStore.toastStoreHouse.findById(id);
            currentToastStore?.updateToast(toast);
        })
    );

    /**
     * Hides a floating toast from the toaster. However, this
     * toast is still accessible from the toast panel.
     *
     * @param param0.id The id of the toast to be hidden
     * from the toaster.
     */
    const hideFloatingToast = async ({ id }: Pick<ToastStore, 'id'>) => {
        rootStore.toastStoreHouse.dismissById(id);
    };

    /**
     * Hides all floating toasts from the toaster. However,
     * the toasts are all still accessible from the toast panel.
     */
    const hideAllFloatingToasts = async () => {
        rootStore.toastStoreHouse.dismissAll();
    };

    /**
     * Removes a toast from the system This happens in the case where
     * the toast is dismissed from inside the toast panel.
     *
     * @param param0.id The id of the toast to be removed.
     */
    const removeToast = async ({ id }: Pick<ToastStore, 'id'>) => {
        rootStore.toastStoreHouse.removeById(id);
    };

    /**
     * Removes all the toasts in the system. This happens
     * when the dismiss all button is clicked in the
     * toast panel.
     */
    const removeAllToasts = async () => {
        rootStore.toastStoreHouse.setItems([]);
    };

    /**
     * Marks all the toasts as seen, thus reflecting in the
     * header icon badge number.
     */
    const markAllToastsAsSeen = async () => {
        rootStore.toastStoreHouse.markAllSeen();

        await hideAllFloatingToasts();
    };

    return {
        hideFloatingToast,
        removeAllToasts,
        removeToast,
        markAllToastsAsSeen
    };
};
